import type {
  AttackWave,
  Country,
  FixedDefence,
  MobileDefence,
  Road,
  ScenarioConfig,
  Sensor,
  StrikeWeapon,
  Target,
  TargetCategory,
  TargetDistribution,
  Vec2,
  World,
} from "./types.js";
import { makeRng, rngPick, rngRange, type RNG } from "./rng.js";

const FIXED_UNIT_COST = 100;
const MOBILE_UNIT_COST = 150;
const FIXED_BASE_RANGE = 180;
const FIXED_RELOAD_TIME = 1.5;
const FIXED_PKILL = 0.7;
const MOBILE_SENSOR_RANGE = 280;
const MOBILE_ENGAGEMENT_RANGE = 140;
const MOBILE_MAGAZINE = 4;
const MOBILE_RELOAD_TIME = 1.0;
const MOBILE_PKILL = 0.6;
const MOBILE_ENDURANCE = 1800;
const SENSOR_BASE_RANGE = 320;
const SENSOR_DETECTION_P = 0.94;
const SENSOR_TRACK_QUALITY = 0.8;
const STRIKE_SPEED = 220;
const STRIKE_PDAMAGE = 0.85;
const STRIKE_COST = 60;

const CATEGORIES: TargetCategory[] = [
  "capital",
  "airbase",
  "power",
  "rail",
  "factory",
  "depot",
  "bridge",
  "radar",
  "port",
];

export function buildScenario(config: ScenarioConfig): {
  world: World;
  waves: AttackWave[];
} {
  const rng = makeRng(config.seed);
  const targets = generateTargets(config, rng);
  const country = generateCountry(targets, config, rng);
  const roads = generateRoads(targets, rng);
  const sensors = generateSensors(config, rng);
  const { fixedDefences, mobileDefences } = allocateDefences(config, targets);
  const world: World = {
    width: config.width,
    height: config.height,
    targets,
    fixedDefences,
    mobileDefences,
    sensors,
    country,
    roads,
  };
  const waves = generateWaves(config, world, rng);
  return { world, waves };
}

function generateTargets(config: ScenarioConfig, rng: RNG): Target[] {
  const positions = generateTargetPositions(config, rng);
  const values = generateTargetValues(config, rng, positions.length);
  const usedNames = new Set<string>();
  const ranked = [...values].sort((a, b) => b - a);
  const top4 = new Set(ranked.slice(0, Math.min(4, ranked.length)));
  const top1 = ranked[0];
  const targets: Target[] = positions.map((pos, i) => {
    const category = rngPick(rng, CATEGORIES);
    const hardness = clamp(rngRange(rng, 0.1, 0.5), 0, 0.9);
    const value = values[i] ?? 0;
    const isCapital = value === top1 && i === values.indexOf(top1);
    const isMajor = top4.has(value);
    let name = generateSettlementName(rng, isCapital);
    let tries = 0;
    while (usedNames.has(name) && tries < 10) {
      name = generateSettlementName(rng, isCapital);
      tries += 1;
    }
    usedNames.add(name);
    return {
      id: `tgt_${i.toString().padStart(3, "0")}`,
      name,
      pos,
      value,
      hardness,
      destroyed: false,
      category,
      isCapital,
      isMajor,
    };
  });
  return targets;
}

const NAME_PREFIXES = ["North", "South", "East", "West", "Upper", "Lower", "Great", "Little", "Old", "New", "Saint", "Port"];
const NAME_STARTS = ["Br", "Cr", "Dr", "Fl", "Gr", "Pr", "Sl", "St", "Tr", "Wh", "Th", "Sh", "Bl", "Kn", "M", "K", "L", "R", "B", "H", "P", "W", "F", "C", "T", "D", "G"];
const NAME_VOWELS = ["a", "e", "i", "o", "u", "ai", "ea", "ou", "y"];
const NAME_STEMS = ["ridge", "field", "ford", "ham", "ton", "burgh", "haven", "wick", "moor", "stoke", "bury", "wood", "stead", "thorpe", "by", "ness", "rock", "vale", "marsh", "cliff", "mouth", "pool", "minster", "well", "shire", "den", "combe", "worth"];
const NAME_TWOPART_VOWEL = ["a", "e", "i", "o"];

function generateSettlementName(rng: RNG, isCapital: boolean): string {
  const r = rng();
  if (r < 0.25 || isCapital) {
    const prefix = rngPick(rng, NAME_PREFIXES);
    const stem = rngPick(rng, NAME_STEMS);
    return `${prefix} ${capitalize(stem)}`;
  }
  if (r < 0.6) {
    const start = rngPick(rng, NAME_STARTS);
    const vowel = rngPick(rng, NAME_VOWELS);
    const stem = rngPick(rng, NAME_STEMS);
    return capitalize(start.toLowerCase() + vowel + stem);
  }
  const start = rngPick(rng, NAME_STARTS);
  const vowel1 = rngPick(rng, NAME_TWOPART_VOWEL);
  const stem1 = rngPick(rng, NAME_STEMS);
  const stem2 = rngPick(rng, NAME_STEMS);
  return capitalize(start.toLowerCase() + vowel1 + stem1) + capitalize(stem2);
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

function generateCountry(targets: Target[], config: ScenarioConfig, rng: RNG): Country {
  if (targets.length === 0) {
    return { outline: [] };
  }
  const cx = targets.reduce((s, t) => s + t.pos.x, 0) / targets.length;
  const cy = targets.reduce((s, t) => s + t.pos.y, 0) / targets.length;
  const N = 28;
  const outline: Vec2[] = [];
  for (let i = 0; i < N; i++) {
    const angle = (i / N) * Math.PI * 2;
    let maxProj = 0;
    for (const t of targets) {
      const dx = t.pos.x - cx;
      const dy = t.pos.y - cy;
      const proj = dx * Math.cos(angle) + dy * Math.sin(angle);
      if (proj > maxProj) maxProj = proj;
    }
    const baseMargin = Math.min(config.width, config.height) * 0.08;
    const jitter = rngRange(rng, 0.85, 1.25);
    const radius = (maxProj + baseMargin) * jitter;
    let x = cx + Math.cos(angle) * radius;
    let y = cy + Math.sin(angle) * radius;
    const pad = Math.min(config.width, config.height) * 0.025;
    x = clamp(x, pad, config.width - pad);
    y = clamp(y, pad, config.height - pad);
    outline.push({ x, y });
  }
  return { outline };
}

function generateRoads(targets: Target[], rng: RNG): Road[] {
  if (targets.length < 2) return [];
  const majors = targets.filter((t) => t.isMajor);
  const seen = new Set<string>();
  const roads: Road[] = [];
  const edgeKey = (a: Target, b: Target): string =>
    a.id < b.id ? `${a.id}|${b.id}` : `${b.id}|${a.id}`;

  for (let i = 0; i < majors.length; i++) {
    for (let j = i + 1; j < majors.length; j++) {
      const a = majors[i];
      const b = majors[j];
      if (!a || !b) continue;
      const key = edgeKey(a, b);
      if (seen.has(key)) continue;
      seen.add(key);
      roads.push({ waypoints: meanderPath(a.pos, b.pos, rng, "motorway"), kind: "motorway" });
    }
  }

  for (const t of targets) {
    const others = targets
      .filter((o) => o.id !== t.id)
      .map((o) => ({ o, d: Math.hypot(o.pos.x - t.pos.x, o.pos.y - t.pos.y) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, t.isMajor ? 5 : 4);
    for (const { o } of others) {
      const key = edgeKey(t, o);
      if (seen.has(key)) continue;
      seen.add(key);
      roads.push({ waypoints: meanderPath(t.pos, o.pos, rng, "secondary"), kind: "secondary" });
    }
  }

  return roads;
}

function meanderPath(a: Vec2, b: Vec2, rng: RNG, kind: "motorway" | "secondary"): Vec2[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return [{ ...a }, { ...b }];
  const segmentLen = kind === "motorway" ? 50 : 32;
  const numIntermediate = Math.max(2, Math.round(len / segmentLen));
  const perpX = -dy / len;
  const perpY = dx / len;
  const maxJitter = kind === "motorway" ? Math.min(len * 0.08, 35) : Math.min(len * 0.18, 30);
  const waypoints: Vec2[] = [{ ...a }];
  let lastJitter = 0;
  for (let i = 1; i <= numIntermediate; i++) {
    const t = i / (numIntermediate + 1);
    const baseX = a.x + dx * t;
    const baseY = a.y + dy * t;
    const ramp = Math.min(t, 1 - t) * 2;
    const target = (rng() - 0.5) * 2 * maxJitter * ramp;
    const jitter = lastJitter * 0.55 + target * 0.45;
    lastJitter = jitter;
    waypoints.push({ x: baseX + perpX * jitter, y: baseY + perpY * jitter });
  }
  waypoints.push({ ...b });
  return waypoints;
}

function generateTargetPositions(config: ScenarioConfig, rng: RNG): Vec2[] {
  const out: Vec2[] = [];
  const { width, height, targetCount, targetDistribution } = config;
  const margin = Math.min(width, height) * 0.08;
  if (targetDistribution === "concentrated") {
    const center: Vec2 = {
      x: width * 0.55 + rngRange(rng, -width * 0.05, width * 0.05),
      y: height * 0.5 + rngRange(rng, -height * 0.05, height * 0.05),
    };
    const radius = Math.min(width, height) * 0.12;
    for (let i = 0; i < targetCount; i++) {
      const a = rng() * Math.PI * 2;
      const r = Math.sqrt(rng()) * radius;
      out.push({
        x: clamp(center.x + Math.cos(a) * r, margin, width - margin),
        y: clamp(center.y + Math.sin(a) * r, margin, height - margin),
      });
    }
  } else if (targetDistribution === "clustered") {
    const clusterCount = 3;
    const clusters: Vec2[] = [];
    for (let c = 0; c < clusterCount; c++) {
      clusters.push({
        x: rngRange(rng, width * 0.3, width * 0.85),
        y: rngRange(rng, height * 0.2, height * 0.8),
      });
    }
    const radius = Math.min(width, height) * 0.1;
    for (let i = 0; i < targetCount; i++) {
      const cluster = clusters[i % clusterCount];
      if (!cluster) continue;
      const a = rng() * Math.PI * 2;
      const r = Math.sqrt(rng()) * radius;
      out.push({
        x: clamp(cluster.x + Math.cos(a) * r, margin, width - margin),
        y: clamp(cluster.y + Math.sin(a) * r, margin, height - margin),
      });
    }
  } else if (targetDistribution === "country") {
    return countryPositions(config, rng);
  } else {
    for (let i = 0; i < targetCount; i++) {
      out.push({
        x: rngRange(rng, width * 0.3, width - margin),
        y: rngRange(rng, margin, height - margin),
      });
    }
  }
  return out;
}

function countryPositions(config: ScenarioConfig, rng: RNG): Vec2[] {
  const { width, height, targetCount } = config;
  const margin = Math.min(width, height) * 0.05;
  const minDist = Math.min(width, height) * 0.07;
  const positions: Vec2[] = [];

  positions.push({
    x: width * 0.55 + rngRange(rng, -width * 0.04, width * 0.04),
    y: height * 0.5 + rngRange(rng, -height * 0.06, height * 0.06),
  });

  const majorCount = Math.min(3, Math.max(1, Math.round(targetCount * 0.18)));
  for (let i = 0; i < majorCount && positions.length < targetCount; i++) {
    const baseAngle = (i / majorCount) * Math.PI * 2 + rng() * 0.6;
    const r = rngRange(rng, width * 0.16, width * 0.28);
    const cap = positions[0];
    if (!cap) break;
    const candidate: Vec2 = {
      x: clamp(cap.x + Math.cos(baseAngle) * r, width * 0.27, width - margin),
      y: clamp(cap.y + Math.sin(baseAngle) * r, margin, height - margin),
    };
    if (positions.every((p) => Math.hypot(candidate.x - p.x, candidate.y - p.y) >= minDist)) {
      positions.push(candidate);
    }
  }

  let attempts = 0;
  while (positions.length < targetCount && attempts < 8000) {
    attempts++;
    const candidate: Vec2 = {
      x: rngRange(rng, width * 0.27, width - margin),
      y: rngRange(rng, margin, height - margin),
    };
    let ok = true;
    for (const p of positions) {
      if (Math.hypot(candidate.x - p.x, candidate.y - p.y) < minDist) {
        ok = false;
        break;
      }
    }
    if (ok) positions.push(candidate);
  }
  return positions;
}

function generateTargetValues(config: ScenarioConfig, rng: RNG, n: number): number[] {
  const entropy = clamp(config.targetValueEntropy, 0, 1);
  const exponent = (1 - entropy) * 4 + 0.001;
  const raw: number[] = [];
  for (let i = 0; i < n; i++) raw.push(Math.pow(rng() + 0.001, exponent));
  raw.sort((a, b) => b - a);
  const total = 1000;
  const sum = raw.reduce((s, v) => s + v, 0) || 1;
  const shuffled = shuffle(rng, raw);
  return shuffled.map((v) => (v / sum) * total);
}

function generateSensors(config: ScenarioConfig, rng: RNG): Sensor[] {
  const range = SENSOR_BASE_RANGE * config.warningRangeMultiplier;
  const positions: Vec2[] = [
    { x: config.width * 0.15, y: config.height * 0.25 },
    { x: config.width * 0.15, y: config.height * 0.75 },
    { x: config.width * 0.45, y: config.height * 0.5 },
  ];
  return positions.map((pos, i) => ({
    id: `sen_${i}`,
    pos: jitter(pos, rng, 20),
    range,
    detectionProbability: SENSOR_DETECTION_P,
    trackQuality: SENSOR_TRACK_QUALITY,
  }));
}

function allocateDefences(
  config: ScenarioConfig,
  targets: Target[],
): { fixedDefences: FixedDefence[]; mobileDefences: MobileDefence[] } {
  const totalBudget = config.totalDefenceBudget;
  const mobileShare = clamp(config.mobileDefenceBudgetShare, 0, 1);
  const fixedBudget = totalBudget * (1 - mobileShare);
  const mobileBudget = totalBudget * mobileShare;
  const fixedCount = Math.floor(fixedBudget / FIXED_UNIT_COST);
  const mobileCount = Math.floor(mobileBudget / MOBILE_UNIT_COST);

  const ranked = [...targets].sort((a, b) => b.value - a.value);

  const fixedDefences: FixedDefence[] = [];
  for (let i = 0; i < fixedCount; i++) {
    const t = ranked[i % ranked.length];
    if (!t) break;
    fixedDefences.push({
      id: `fix_${i.toString().padStart(2, "0")}`,
      pos: { x: t.pos.x, y: t.pos.y },
      range: FIXED_BASE_RANGE,
      magazine: config.fixedDefenceMagazine,
      reloadTime: FIXED_RELOAD_TIME,
      pKill: FIXED_PKILL,
      cost: FIXED_UNIT_COST,
      reloadUntil: 0,
      shotsFired: 0,
    });
  }

  const mobileDefences: MobileDefence[] = [];
  for (let i = 0; i < mobileCount; i++) {
    const t = ranked[i % ranked.length];
    if (!t) break;
    const offsetAngle = (i / Math.max(1, mobileCount)) * Math.PI * 2;
    const offsetR = Math.min(config.width, config.height) * 0.06;
    const patrolPoint: Vec2 = {
      x: t.pos.x + Math.cos(offsetAngle) * offsetR,
      y: t.pos.y + Math.sin(offsetAngle) * offsetR,
    };
    mobileDefences.push({
      id: `mob_${i.toString().padStart(2, "0")}`,
      pos: { x: patrolPoint.x, y: patrolPoint.y },
      patrolPoint,
      speed: config.mobileDefenceSpeed,
      sensorRange: MOBILE_SENSOR_RANGE,
      engagementRange: MOBILE_ENGAGEMENT_RANGE,
      magazine: MOBILE_MAGAZINE,
      reloadTime: MOBILE_RELOAD_TIME,
      endurance: MOBILE_ENDURANCE,
      pKill: MOBILE_PKILL,
      cost: MOBILE_UNIT_COST,
      reloadUntil: 0,
      shotsFired: 0,
      lockedThreatId: null,
      cachedScreenPoint: null,
    });
  }

  return { fixedDefences, mobileDefences };
}

function generateWaves(config: ScenarioConfig, world: World, rng: RNG): AttackWave[] {
  const waves: AttackWave[] = [];
  const concentration = clamp(config.attackerConcentration, 0, 1);
  const ranked = [...world.targets].sort((a, b) => b.value - a.value);
  const numTargetsAttacked = Math.max(
    1,
    Math.round((1 - concentration) * (ranked.length - 1) + 1),
  );
  const chosen = ranked.slice(0, numTargetsAttacked);
  const totalChosenValue = chosen.reduce((s, t) => s + t.value, 0) || 1;

  for (let w = 0; w < config.attackerWaveCount; w++) {
    const launchArea: Vec2 = {
      x: rngRange(rng, 0, config.width * 0.08),
      y: rngRange(rng, config.height * 0.15, config.height * 0.85),
    };
    const weapons: StrikeWeapon[] = [];
    for (let i = 0; i < config.attackerWeaponsPerWave; i++) {
      const target = pickWeightedTarget(rng, chosen, totalChosenValue);
      const offset = jitter({ x: 0, y: 0 }, rng, 30);
      weapons.push({
        id: `wpn_${w}_${i.toString().padStart(3, "0")}`,
        pos: { x: launchArea.x + offset.x, y: launchArea.y + offset.y },
        origin: { x: launchArea.x + offset.x, y: launchArea.y + offset.y },
        speed: STRIKE_SPEED,
        targetId: target.id,
        detected: false,
        destroyed: false,
        pDamage: STRIKE_PDAMAGE,
        cost: STRIKE_COST,
        launchedAt: 0,
        detectedAt: null,
        terminalAt: null,
      });
    }
    waves.push({
      id: `wave_${w}`,
      startTime: w * config.attackerWaveSpacing,
      launchArea,
      weapons,
      doctrine: config.attackerDoctrine,
      launched: false,
    });
  }
  return waves;
}

function pickWeightedTarget(rng: RNG, targets: Target[], totalValue: number): Target {
  const r = rng() * totalValue;
  let acc = 0;
  for (const t of targets) {
    acc += t.value;
    if (r <= acc) return t;
  }
  return targets[targets.length - 1] as Target;
}

function jitter(p: Vec2, rng: RNG, amount: number): Vec2 {
  return {
    x: p.x + rngRange(rng, -amount, amount),
    y: p.y + rngRange(rng, -amount, amount),
  };
}

function shuffle<T>(rng: RNG, arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = out[i] as T;
    const b = out[j] as T;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export function defaultScenarioConfig(overrides: Partial<ScenarioConfig> = {}): ScenarioConfig {
  return {
    seed: 1,
    width: 1200,
    height: 800,
    targetCount: 12,
    targetDistribution: "country",
    targetValueEntropy: 0.5,
    totalDefenceBudget: 1200,
    mobileDefenceBudgetShare: 0.4,
    warningRangeMultiplier: 1,
    attackerWaveCount: 4,
    attackerWaveSpacing: 2.5,
    attackerWeaponsPerWave: 6,
    attackerConcentration: 0.6,
    attackerDoctrine: "concentrate",
    fixedDefenceMagazine: 8,
    mobileDefenceSpeed: 100,
    ...overrides,
  };
}
